import { inject, Injectable, signal } from '@angular/core';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { HttpClient } from '@angular/common/http';
import { AccountBalance } from '../../common/general-types/wallet.dto';

export type { AccountBalance } from '../../common/general-types/wallet.dto';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  PENDING_WALLET_TOPUP_KEY,
  rememberPendingWalletTopUp,
  takePendingWalletTopUp,
  WALLET_TOPUP_SUCCESS_PARAM,
  withWalletTopUpSuccess,
  WALLET_TOPUP_SESSION_PARAM,
} from '../analytics/customer-analytics';
import { PosthogService } from '../analytics/posthog.service';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { AdConversionsService } from '@/app/services/analytics/ad-conversions.service';
import { filter, take as takeFirst } from 'rxjs/operators';
import { firstValueFrom as firstValue } from 'rxjs';

export interface WalletTopUpTarget {
  businessAccountId: string;
  currencyCode?: string;
  companyName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class WalletService {

  topUpAmountOptions: number[] = [
    250,
    500,
    1000,
    2500,
    5000,
  ];


  currentBalance = signal<number | undefined>(undefined);
  availableBalance = signal<number | undefined>(undefined);
  currencyCode = signal<string | undefined>(undefined);
  balanceBusinessAccountId = signal<string | undefined>(undefined);

  topUpDrawerOpen = signal<boolean>(false);
  topUpTarget = signal<WalletTopUpTarget | null>(null);

  private http = inject(HttpClient);
  private firebaseService = inject(FirebaseService);
  private posthogService = inject(PosthogService);
  private adConversions = inject(AdConversionsService);
  private currentUserService = inject(CurrentUserService);

  /** HTTP-only fetch. Does not update the navbar wallet signals. */
  fetchBalance(businessAccountId: string): Observable<AccountBalance> {
    const wallet_balance_url = `${environment.shout_api.url}/wallet/balance/${businessAccountId}`;
    return this.http.get<AccountBalance>(wallet_balance_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getBalance(businessAccountId: string): Observable<AccountBalance> {
    return this.fetchBalance(businessAccountId).pipe(tap(balanceRes => {
      console.log('👮🏻‍♂️👮🏻‍♂️ Balance Response:', balanceRes);
      if (this.currentUserService.focussedBrand?.id !== businessAccountId) {
        return;
      }
      this.balanceBusinessAccountId.set(businessAccountId);
      this.availableBalance.set(balanceRes.availableBalance / 100);
      this.currentBalance.set(balanceRes.currentBalance / 100);
      this.currencyCode.set(balanceRes.currencyCode);
    }));
  }

  /**
   * Take a reservation off the balance on screen without refetching it.
   *
   * The hire already told us the exact amount it reserved, so a round trip only adds
   * latency. Applied whenever the balance on screen is this account's, which is a
   * tighter check than the focussed brand: a brief can be managed from another brand.
   */
  reserve(businessAccountId: string, amount: number): void {
    if (!Number.isFinite(amount) || amount <= 0) {
      return;
    }
    if (this.balanceBusinessAccountId() !== businessAccountId) {
      return;
    }
    // Both figures are pounds from pence, so round back to pence to keep float noise out.
    this.availableBalance.update((available) =>
      available == null ? available : Math.round((available - amount) * 100) / 100,
    );
  }

  openTopUpDrawer(target?: WalletTopUpTarget | null): void {
    this.topUpTarget.set(target ?? null);
    this.topUpDrawerOpen.set(true);
  }

  closeTopUpDrawer(): void {
    this.topUpDrawerOpen.set(false);
    this.topUpTarget.set(null);
  }

  getTransactions(businessAccountId: string, search?: string) {
    let url = `${environment.shout_api.url}/wallet/wallet-transactions-modern/${businessAccountId}`;
    if (search?.trim()) url += `?search=${encodeURIComponent(search.trim())}`;
    return this.http.get(url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  walletTopupRequest(businessAccountId: string, amount: number) {
    const wallet_topup_request_url = `${environment.shout_api.url}/wallet-topup-requests`;
    return this.http.post(wallet_topup_request_url, { businessAccountId, amount }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  topUpLinkStripe(amount: number, currencyCode: string, firebaseUid: string, businessAccountId: string, redirectUrl: string) {
    const wallet_topup_stripe_url = `${environment.shout_api.url}/wallet/top-up/stripe`;
    rememberPendingWalletTopUp({
      amount,
      currency: currencyCode,
      businessAccountId,
    });
    this.posthogService.posthog.capture('wallet_topup_initiated', {
      amount,
      currency_code: currencyCode,
      business_account_id: businessAccountId,
    });
    return this.http.post(wallet_topup_stripe_url, {
      amount,
      currencyCode,
      firebaseUid,
      businessAccountId,
      successUrl: withWalletTopUpSuccess(redirectUrl),
      cancelUrl: redirectUrl
    }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  captureCompletedTopUpFromReturn(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const url = new URL(window.location.href);
    if (url.searchParams.get(WALLET_TOPUP_SUCCESS_PARAM) !== 'success') {
      sessionStorage.removeItem(PENDING_WALLET_TOPUP_KEY);
      return;
    }
    const pending = takePendingWalletTopUp();
    const sessionId = url.searchParams.get(WALLET_TOPUP_SESSION_PARAM);
    url.searchParams.delete(WALLET_TOPUP_SUCCESS_PARAM);
    url.searchParams.delete(WALLET_TOPUP_SESSION_PARAM);
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, '', next);
    if (sessionId && sessionId !== '{CHECKOUT_SESSION_ID}') {
      void this.confirmTopUpConversion(sessionId);
    }
    if (!pending) {
      return;
    }
    this.posthogService.posthog.capture('wallet_topup_completed', {
      revenue: pending.amount,
      currency: pending.currency,
      product: 'wallet_topup',
      business_account_id: pending.businessAccountId,
    });
  }

  /**
   * Fires the wallet top-up ad conversion only once our backend (via the Stripe webhook)
   * has marked the payment as succeeded. Polls briefly because the webhook can land a
   * few seconds after Stripe redirects back. Value and currency come from the backend.
   */
  private async confirmTopUpConversion(sessionId: string): Promise<void> {
    if (!this.adConversions.isActive()) {
      return;
    }
    try {
      await firstValue(this.firebaseService.authStateChecked$.pipe(filter(Boolean), takeFirst(1)));
    } catch {
      return;
    }
    const url = `${environment.shout_api.url}/ad-tracking/top-up/${encodeURIComponent(sessionId)}`;
    for (let attempt = 0; attempt < 8; attempt++) {
      try {
        const status = await firstValue(this.http.get<{ status: string; amount: number; currencyCode: string }>(url));
        if (status?.status === 'succeeded') {
          this.adConversions.trackTopUp(sessionId, status.amount, status.currencyCode);
          return;
        }
        if (status?.status === 'failed') {
          return;
        }
      } catch {
        /* not visible yet or transient error: retry */
      }
      await new Promise((resolve) => setTimeout(resolve, 2500));
    }
  }

  getTransactionsByApplication(briefApplicationId: string) {
    const url = `${environment.shout_api.url}/wallet-transactions/all-by-brief-application/${briefApplicationId}`;
    return this.http.get(url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }
}
